import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, ChevronLeft, Box } from 'lucide-react';
import { getCollections, getCollection } from '../../services/api';
import ImageCarousel from '../../components/ImageCarousel';

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await getCollections();
        setCollections(response.data.collections);
      } catch (error) {
        console.error('Failed to fetch collections:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  const handleViewCollection = async (id) => {
    setDetailLoading(true);
    try {
      const response = await getCollection(id);
      setSelectedCollection(response.data);
    } catch (error) {
      console.error('Failed to fetch collection:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedCollection(null);
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Detail View
  if (selectedCollection) {
    return (
      <div className="p-6 md:p-12" data-testid="collection-detail">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4 -ml-2"
          data-testid="back-to-collections-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Collections
        </Button>

        <div className="max-w-4xl">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-testid="collection-title">
                {selectedCollection.title}
              </h1>

              {/* Image Carousel */}
              <div className="mb-8">
                <ImageCarousel images={selectedCollection.images} alt={selectedCollection.title} />
              </div>

              {/* Description */}
              <Card className="border-gray-100 mb-6">
                <CardContent className="p-6">
                  <p className="text-lg text-gray-700 leading-relaxed" data-testid="collection-description">
                    {selectedCollection.description}
                  </p>
                </CardContent>
              </Card>

              {/* 3D Model Placeholder */}
              {selectedCollection.has3DModel && (
                <Card className="border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-testid="3d-model-placeholder">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Box className="h-6 w-6 text-gray-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">3D View Coming Soon</h3>
                        <p className="text-sm text-gray-500">
                          An interactive 3D model of this item will be available in a future update.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="p-6 md:p-12" data-testid="collections-page">
      <div className="max-w-4xl">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Collections
        </h1>
        <p className="text-gray-600 mb-8">
          Explore the historical artefacts and ceremonial objects of St David (University Lodge) No. 36.
        </p>

        {/* Collections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="collections-list">
          {collections.map((collection) => (
            <Card
              key={collection.id}
              className="border-gray-100 hover:shadow-md transition-all cursor-pointer overflow-hidden"
              onClick={() => handleViewCollection(collection.id)}
              data-testid={`collection-card-${collection.id}`}
            >
              <div className="aspect-video overflow-hidden bg-gray-100">
                {collection.images && collection.images[0] && (
                  <img
                    src={collection.images[0]}
                    alt={collection.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                )}
              </div>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">{collection.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm line-clamp-2">{collection.description}</p>
                {collection.has3DModel && (
                  <div className="flex items-center gap-1 mt-3 text-xs text-gray-500">
                    <Box className="h-3 w-3" />
                    <span>3D model available</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CollectionsPage;
